import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import './GalleryVrac.css';

const isVideo = (file) =>
  /\.(mp4|webm|ogg)$/i.test(file);

const videoIcon = (
  <div className="video-thumbnail">
    <svg width="48" height="48" viewBox="0 0 48 48" fill="#fff">
      <circle cx="24" cy="24" r="24" fill="rgba(0,0,0,0.4)" />
      <polygon points="18,15 36,24 18,33" fill="#fff"/>
    </svg>
  </div>
);

const GalleryVrac = ({ title, description }) => {
  const { t } = useTranslation();
  const items = t('galleryVrac.items', { returnObjects: true });

  const [lightboxIndex, setLightboxIndex] = useState(null);
  const itemRefs = useRef([]);

  const openLightbox = (idx) => setLightboxIndex(idx);
  const closeLightbox = () => setLightboxIndex(null);
  const next = () => setLightboxIndex((lightboxIndex + 1) % items.length);
  const prev = () => setLightboxIndex((lightboxIndex - 1 + items.length) % items.length);

  useEffect(() => {
    if (lightboxIndex === null) return;
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight') next();
      else if (e.key === 'ArrowLeft') prev();
      else if (e.key === 'Escape') closeLightbox();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxIndex, items.length]);

  // Tempo pour les images dans la lightbox
  useEffect(() => {
    if (lightboxIndex === null) return;
    if (!isVideo(items[lightboxIndex].file)) {
      const timer = setTimeout(next, 2000);
      return () => clearTimeout(timer);
    }
  }, [lightboxIndex, items]);

  const getRatioClass = ratio => {
    if (ratio === 'landscape') return 'masonry-item landscape';
    if (ratio === 'portrait') return 'masonry-item portrait';
    if (ratio === 'square') return 'masonry-item square';
    return 'masonry-item';
  };

  return (
    <section className="gallery-vrac-section" style={{ backgroundColor: "#f7f7f7" }}>
      <h2>{title}</h2>
      <p dangerouslySetInnerHTML={{ __html: description }} />
      <div className="gallery-vrac-masonry">
        {items.map((item, idx) => (
          <div
            key={idx}
            className={getRatioClass(item.ratio)}
            onClick={() => openLightbox(idx)}
          >
            <div className="image-container">
              {isVideo(item.file) ? (
                <video
                  src={item.file}
                  poster={item.poster}
                  className={item.ratio === 'landscape' ? 'video-landscape' : ''}
                  style={{ pointerEvents: 'none' }}
                  tabIndex={-1}
                  muted
                  preload="metadata"
                  // Pas de controls, pas d'autoplay
                />
              ) : (
                <img
                  src={item.file}
                  alt={`media ${idx + 1}`}
                  className="gallery-img"
                  loading="lazy"
                />
              )}
            </div>
          </div>
        ))}
      </div>
      {lightboxIndex !== null && (
        <div className="lightbox" onClick={e => {
          if (e.target.classList.contains('lightbox')) closeLightbox();
        }}>
          <button className="close" onClick={closeLightbox} aria-label="Fermer">✖</button>
          <span className="arrow left" onClick={prev}>‹</span>
          <div className="lightbox-content">
            {isVideo(items[lightboxIndex].file) ? (
              <video
                src={items[lightboxIndex].file}
                autoPlay
                poster={items[lightboxIndex].poster}
                style={{ maxWidth: '90vw', maxHeight: '80vh' }}
                className={items[lightboxIndex].ratio === 'landscape' ? 'video-landscape' : ''}
                muted
                onEnded={next}
                preload="auto"
              />
            ) : (
              <img
                src={items[lightboxIndex].file}
                alt={`media ${lightboxIndex + 1}`}
                style={{
                  maxWidth: '90vw',
                  maxHeight: '80vh',
                  objectFit: 'contain'
                }}
                className="lightbox-img"
                loading="lazy"
              />
            )}
          </div>
          <span className="arrow right" onClick={next}>›</span>
        </div>
      )}
    </section>
  );
};

export default GalleryVrac;

